
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import * as pdfjsLib from 'pdfjs-dist';
import styles from './PdfViewer.module.scss';

interface PdfViewerProps {
  pdfFile: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ pdfFile }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);  
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [lastPinchDistance, setLastPinchDistance] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);

  // Set PDF.js worker source
  useEffect(() => {
    const checkWorkerAccessibility = async () => {
      try {
        // Check if local worker is accessible
        const response = await fetch('/pdf.worker.min.js');
        if (response.ok) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
        } else {
          throw new Error('Local worker not accessible');
        }
      } catch (error) {
        // Fallback to CDN
        try {
          pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js';
        } catch (fallbackError) {
          // Silently fail if both options don't work
        }
      }
    };

    checkWorkerAccessibility();
  }, []);

  const handleDocumentLoadSuccess = () => {
    setLoading(false);
    setError(null);
  };

  // Helper function to get distance between two touches
  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  // Helper function to constrain translation within bounds
  const constrainTranslation = useCallback((newX: number, newY: number, currentScale: number) => {
    if (currentScale <= 1.1) return { x: 0, y: 0 }; // Keep centered for small scales

    // Calculate the maximum allowed translation based on scale
    const container = containerRef.current;
    if (!container) return { x: newX, y: newY };

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Calculate how much the scaled content extends beyond the container
    const maxX = (containerWidth * (currentScale - 1)) / 2;
    const maxY = (containerHeight * (currentScale - 1)) / 2;

    return {
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY))
    };
  }, []);

  // Handle wheel zoom (desktop)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(0.5, Math.min(5, scale * zoomFactor));

      // When zooming out, behave like loading on smaller screen - fit to container
      if (newScale <= 1) {
        // Reset to center/fit when at or below 100% (like smaller screen)
        setTranslateX(0);
        setTranslateY(0);
      } else {
        // For zoom in, maintain proportional position
        const scaleRatio = newScale / scale;
        const newTranslateX = translateX * scaleRatio;
        const newTranslateY = translateY * scaleRatio;
        
        setTranslateX(newTranslateX);
        setTranslateY(newTranslateY);
      }

      setScale(newScale);
    }
  }, [scale, translateX, translateY]);

  // Handle touch start (mobile)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      setLastPinchDistance(getTouchDistance(e.touches));
    } else if (e.touches.length === 1) {
      // Single touch for panning
      const touch = e.touches[0];
      lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
      setIsDragging(true);
    }
  }, []);

  // Handle touch move (mobile)
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();

    if (e.touches.length === 2 && lastPinchDistance > 0) {
      // Pinch zoom
      const currentDistance = getTouchDistance(e.touches);
      const scaleFactor = currentDistance / lastPinchDistance;
      const newScale = Math.max(0.5, Math.min(5, scale * scaleFactor));

      // When zooming out, behave like loading on smaller screen - fit to container
      if (newScale <= 1) {
        // Reset to center/fit when at or below 100% (like smaller screen)
        setTranslateX(0);
        setTranslateY(0);
      } else {
        // For zoom in, maintain proportional position
        const scaleRatio = newScale / scale;
        const newTranslateX = translateX * scaleRatio;
        const newTranslateY = translateY * scaleRatio;
        
        setTranslateX(newTranslateX);
        setTranslateY(newTranslateY);
      }

      setScale(newScale);
      setLastPinchDistance(currentDistance);
    } else if (e.touches.length === 1 && isDragging && lastTouchRef.current && scale > 1) {
      // Panning when zoomed in
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastTouchRef.current.x;
      const deltaY = touch.clientY - lastTouchRef.current.y;

      const newX = translateX + deltaX;
      const newY = translateY + deltaY;
      const constrained = constrainTranslation(newX, newY, scale);

      setTranslateX(constrained.x);
      setTranslateY(constrained.y);

      lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
    }
  }, [isDragging, lastPinchDistance, scale, translateX, translateY, constrainTranslation]);

  // Handle touch end (mobile)
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setLastPinchDistance(0);
    lastTouchRef.current = null;
  }, []);

  // Handle mouse events for desktop panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      lastTouchRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [scale]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && lastTouchRef.current && scale > 1) {
      const deltaX = e.clientX - lastTouchRef.current.x;
      const deltaY = e.clientY - lastTouchRef.current.y;

      const newX = translateX + deltaX;
      const newY = translateY + deltaY;
      const constrained = constrainTranslation(newX, newY, scale);

      setTranslateX(constrained.x);
      setTranslateY(constrained.y);

      lastTouchRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [isDragging, scale, translateX, translateY, constrainTranslation]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    lastTouchRef.current = null;
  }, []);

  // Reset zoom
  const resetZoom = () => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  };

  if (!pdfFile) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="alert alert-warning" role="alert">
          No PDF file provided
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pdfViewerContainer}>
      {/* Loading Indicator */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div
              className={`spinner-border text-primary ${styles.spinner}`}
              role="status"
            ></div>
            <div className={styles.loadingText}>
              Loading PDF...
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <div className="text-center">
            <div className="alert alert-danger">
              <h5>Error Loading PDF</h5>
              <p>{error}</p>
              <button
                className="btn btn-primary me-2"
                onClick={() => {
                  setError(null);
                  setLoading(true);
                }}
              >
                Retry
              </button>
              <a
                href={pdfFile}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary"
              >
                Open in Browser
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Zoom Controls */}
      {!error && !loading && (
        <div className={styles.zoomControls}>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              const newScale = Math.max(0.5, scale * 0.8);
              
              // When zooming out, behave like loading on smaller screen - fit to container
              if (newScale <= 1) {
                // Reset to center/fit when at or below 100% (like smaller screen)
                setTranslateX(0);
                setTranslateY(0);
              } else {
                // For zoom in, maintain proportional position
                const scaleRatio = newScale / scale;
                const newTranslateX = translateX * scaleRatio;
                const newTranslateY = translateY * scaleRatio;
                
                setTranslateX(newTranslateX);
                setTranslateY(newTranslateY);
              }
              setScale(newScale);
            }}
          >
            -
          </button>
          <span className={styles.zoomLevel}>{Math.round(scale * 100)}%</span>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              const newScale = Math.min(5, scale * 1.2);
              
              // Adjust translation proportionally to maintain position
              const scaleRatio = newScale / scale;
              const newTranslateX = translateX * scaleRatio;
              const newTranslateY = translateY * scaleRatio;
              
              const constrained = constrainTranslation(newTranslateX, newTranslateY, newScale);
              setTranslateX(constrained.x);
              setTranslateY(constrained.y);
              setScale(newScale);
            }}
          >
            +
          </button>
          <button
            className="btn btn-sm btn-outline-secondary ms-2"
            onClick={resetZoom}
          >
            Reset
          </button>
        </div>
      )}

      {/* PDF Viewer with Zoom */}
      {!error && (
        <div
          ref={containerRef}
          className={styles.pdfViewer}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            ref={viewerRef}
            style={{
              transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
              transformOrigin: 'center center',
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              width: '100%',
              height: '100%',
            }}
          >
            <Worker workerUrl="/pdf.worker.min.js">
              <Viewer
                fileUrl={pdfFile}
                onDocumentLoad={handleDocumentLoadSuccess}
              />
            </Worker>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfViewer;